// ─── Public Status Page — footer ─────────────────────────────────────
//
// WHOSE page this is, what it is, and how fresh the numbers above are. The
// freshness line is deliberately TWO facts: when the SERVER generated the
// payload (generatedAt) and when THIS BROWSER last managed to fetch it
// (fetchedAt). During an outage those diverge, and the gap between them is the
// single most useful thing a reader can know about what they are looking at.
//
// THE COMPANY LINK is a constant, not derived from the hostname. It used to be
// built by stripping "status." off window.location.hostname, which produced no
// link at all on every host but the canonical one — localhost, the int stack,
// any preview — i.e. exactly the situations where a reader is least sure what
// they are looking at. This is C&B's page wherever it answers, so "who is this?"
// always leads to cb.dk. It is the ONE outbound link on the page, and it opens
// in a new tab: someone watching an incident should not lose the page they came
// to read.
//
// The tagline sits here rather than in the header on purpose — it is an identity
// line, and next to the status banner it would compete with the one sentence the
// visitor actually came for.

import { da } from '../i18n/da';
import { fmtDateTime } from '../lib/format';
import type { StatusFooterProps } from '../types';
import { SIcon } from '../ui/SIcon';

/** C&B's own site. Not configurable and not derived: the owner of this page does
 *  not change with the hostname it happens to be served from. */
const CB_SITE_HREF = 'https://www.cb.dk';

export function StatusFooter({ generatedAt, fetchedAt, source, onRefresh, refreshing }: StatusFooterProps) {
  return (
    <footer className="sp-footer">
      <div className="sp-footer-inner sp-container">
        {/* Identity block: who, what, and the line that says what C&B is. The
            link's accessible name is the visible brand text (no aria-label
            overriding it — WCAG 2.5.3), and the icon marks the trip off-site. */}
        <div className="sp-footer-brand">
          <a className="sp-footer-link" href={CB_SITE_HREF} target="_blank" rel="noopener noreferrer">
            {da.brand}
            <SIcon name="external" size={12} />
          </a>
          <p className="sp-footer-note">{da.footerNote}</p>
          <p className="sp-footer-tagline">{da.tagline}</p>
        </div>

        <div className="sp-footer-meta">
          {generatedAt && <span>{da.lastUpdated(fmtDateTime(generatedAt))}</span>}
          {fetchedAt !== null && <span>{da.lastChecked(fmtDateTime(fetchedAt))}</span>}
          {source === 'snapshot' && <span className="sp-pill sp-pill--nodata">{da.degradedTitle}</span>}

          <button
            type="button"
            className="sp-btn sp-btn--ghost sp-btn--sm"
            onClick={onRefresh}
            disabled={refreshing}
          >
            <SIcon
              name={refreshing ? 'spinner' : 'activity'}
              size={14}
              className={refreshing ? 'sp-spin' : undefined}
            />
            {refreshing ? da.loading : da.refresh}
          </button>
        </div>
      </div>
    </footer>
  );
}
